import React, { useMemo, useState } from 'react';
import crmClient from './crmClient';
import { parseCsv } from './csv';

const TARGET_FIELDS = [
  { key: 'first_name', label: 'First name', aliases: ['firstname', 'first', 'fname', 'givenname'] },
  { key: 'last_name', label: 'Last name', aliases: ['lastname', 'last', 'lname', 'surname', 'familyname'] },
  { key: 'email', label: 'Email', aliases: ['email', 'emailaddress', 'workemail'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'cell', 'telephone'] },
  { key: 'title', label: 'Title', aliases: ['title', 'jobtitle', 'role', 'position'] },
  { key: 'company', label: 'Company', aliases: ['company', 'companyname', 'business', 'businessname', 'organization', 'account'] },
  { key: 'city', label: 'City', aliases: ['city', 'town'] },
  { key: 'state', label: 'State', aliases: ['state', 'stateregion', 'region', 'province'] },
  { key: 'zip', label: 'ZIP', aliases: ['zip', 'zipcode', 'postalcode', 'postal', 'postcode'] },
  { key: 'website', label: 'Website', aliases: ['website', 'websiteurl', 'url', 'domain', 'web'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'description', 'comments'] }
];

// Guess a column mapping from header names (handles HubSpot-style headers).
const autoMap = headers => {
  const mapping = {};
  TARGET_FIELDS.forEach(field => {
    const match = headers.find(header => {
      const h = header.toLowerCase().replace(/[^a-z]/g, '');
      return h === field.key.replace(/_/g, '') || (field.aliases || []).some(alias => h === alias || h.includes(alias));
    });
    if (match) {
      mapping[field.key] = match;
    }
  });
  return mapping;
};

const ImportView = ({ meta }) => {
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [type, setType] = useState('prospect');
  const [leadSource, setLeadSource] = useState('HubSpot Import');
  const [duplicateStrategy, setDuplicateStrategy] = useState('merge');
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const contactTypes = (meta && meta.contact_types) || ['prospect', 'investor', 'merchant'];

  const handleParse = text => {
    setError(null);
    setSummary(null);
    const result = parseCsv(text);
    if (!result.headers.length) {
      setError('No CSV content detected.');
      setParsed(null);
      return;
    }
    setParsed(result);
    setMapping(autoMap(result.headers));
  };

  const handleFile = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setRaw(text);
      handleParse(text);
    };
    reader.readAsText(file);
  };

  const mappedRows = useMemo(() => {
    if (!parsed) {
      return [];
    }
    return parsed.records
      .map(record => {
        const row = {};
        TARGET_FIELDS.forEach(field => {
          const col = mapping[field.key];
          if (col && record[col]) {
            row[field.key] = record[col];
          }
        });
        return row;
      })
      .filter(row => row.email || row.phone || row.company);
  }, [parsed, mapping]);

  const runImport = () => {
    if (!mappedRows.length) {
      return;
    }
    setBusy(true);
    setError(null);
    crmClient
      .importRows({ type, lead_source: leadSource, duplicate_strategy: duplicateStrategy, rows: mappedRows })
      .then(payload => {
        setBusy(false);
        setSummary((payload && payload.summary) || null);
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Import failed.');
      });
  };

  return (
    <div className="crm-import">
      <div className="crm-card">
        <h3>Import contacts from CSV</h3>
        <p className="crm-muted">
          Upload or paste CSV data (e.g. a HubSpot export). Matches are detected by email or phone. Choose how duplicates are handled
          below — nothing is ever overwritten.
        </p>
        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Upload CSV file</span>
            <input type="file" accept=".csv,text/csv" onChange={handleFile} />
          </label>
          <label>
            <span>Import as</span>
            <select value={type} onChange={event => setType(event.target.value)}>
              {contactTypes.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Lead source</span>
            <input value={leadSource} onChange={event => setLeadSource(event.target.value)} />
          </label>
          <label>
            <span>Duplicates</span>
            <select value={duplicateStrategy} onChange={event => setDuplicateStrategy(event.target.value)}>
              <option value="merge">Merge (fill gaps, keep all data)</option>
              <option value="skip">Skip existing</option>
            </select>
          </label>
        </div>
        <label className="crm-field-grow">
          <span>…or paste CSV</span>
          <textarea
            rows={4}
            value={raw}
            placeholder="first_name,last_name,email,company&#10;Jane,Doe,jane@acme.com,Acme Industrial"
            onChange={event => {
              setRaw(event.target.value);
              handleParse(event.target.value);
            }}
          />
        </label>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      {parsed && (
        <div className="crm-card">
          <h3>Map columns</h3>
          <p className="crm-muted">
            {parsed.records.length} rows detected · {mappedRows.length} importable (with email, phone, or company).
          </p>
          <div className="crm-map-grid">
            {TARGET_FIELDS.map(field => (
              <label key={field.key}>
                <span>{field.label}</span>
                <select
                  value={mapping[field.key] || ''}
                  onChange={event => setMapping({ ...mapping, [field.key]: event.target.value })}
                >
                  <option value="">— none —</option>
                  {parsed.headers.map(header => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <button type="button" className="crm-btn" disabled={busy || !mappedRows.length} onClick={runImport}>
            {busy ? 'Importing…' : `Import ${mappedRows.length} contacts`}
          </button>
        </div>
      )}

      {summary && (
        <div className="crm-card">
          <h3>Import complete</h3>
          <div className="crm-migrate-summary">
            <span>Rows: <strong>{summary.total}</strong></span>
            <span>Contacts created: <strong>{summary.contacts_created}</strong></span>
            <span>Contacts merged: <strong>{summary.contacts_merged || 0}</strong></span>
            <span>Companies created: <strong>{summary.companies_created}</strong></span>
            <span>Companies merged: <strong>{summary.companies_merged || 0}</strong></span>
            <span>Duplicates skipped: <strong>{summary.duplicates}</strong></span>
            <span>Errors: <strong>{summary.errors}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportView;
